import React, { useState, useEffect } from 'react';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { subscriptionService } from '../../../services/subscriptionService';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import SubscriptionDashboard from './SubscriptionDashboard';

const BillingSection = () => {
  const { subscription, loading, hasProAccess, refreshSubscription } = useSubscription();
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);

  // Check if current user is demo account
  const isDemoAccount = user?.email === 'demo@rovynbudget.com';

  // Check for success/cancel params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billingStatus = params?.get('billing');
    
    if (billingStatus === 'success') {
      setShowSuccess(true);
      refreshSubscription();
      // Clear URL params
      window.history?.replaceState({}, '', '/settings');
      
      setTimeout(() => setShowSuccess(false), 5000);
    } else if (billingStatus === 'canceled') {
      setError('Checkout was canceled. You can try again anytime.');
      setTimeout(() => setError(''), 5000);
    }
  }, [refreshSubscription]);

  const handleDemoToggle = async () => {
    setToggleLoading(true);
    setError('');
    try {
      const { data, error: toggleError } = await subscriptionService?.toggleDemoPlan(user?.email);
      if (toggleError) throw toggleError;
      
      // Refresh subscription to reflect changes
      await refreshSubscription();
      
      // Show success message
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to toggle plan. Please try again.');
    } finally {
      setToggleLoading(false);
    }
  };

  const isPastDue = subscription?.subscription_status === 'past_due';

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-2 p-24">
        <div className="animate-pulse space-y-16">
          <div className="h-6 bg-muted rounded w-1/4"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-24">
      {/* Success Message */}
      {showSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-16 flex items-start gap-12">
          <Icon name="CheckCircle" size={20} color="#16a34a" />
          <div className="flex-1">
            <h4 className="font-semibold text-green-800 mb-4">
              {isDemoAccount ? 'Plan Switched Successfully!' : 'Subscription Activated!'}
            </h4>
            <p className="text-sm text-green-700">
              {isDemoAccount 
                ? `You are now on the ${hasProAccess ? 'Pro' : 'Free'} plan.`
                : 'Welcome to Rovyn Budget Pro! Your subscription is now active and all Pro features are unlocked.'
              }
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-16 flex items-start gap-12">
          <Icon name="AlertCircle" size={20} color="#dc2626" />
          <div className="flex-1">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Past Due Warning */}
      {isPastDue && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-16 flex items-start gap-12">
          <Icon name="AlertTriangle" size={20} color="#ca8a04" />
          <div className="flex-1">
            <h4 className="font-semibold text-yellow-800 mb-4">Payment Past Due</h4>
            <p className="text-sm text-yellow-700 mb-12">
              Your payment is past due. Please update your payment method to continue using Pro features.
            </p>
          </div>
        </div>
      )}

      {/* Demo Account Toggle */}
      {isDemoAccount && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-24">
          <div className="flex items-start justify-between mb-16">
            <div className="flex items-start gap-12">
              <Icon name="Zap" size={20} color="#2563eb" />
              <div>
                <h4 className="font-semibold text-blue-800 mb-4">Demo Account Controls</h4>
                <p className="text-sm text-blue-700">
                  As the demo account, you can freely switch between Free and Pro plans to test all features.
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-12 mt-16">
            <Button
              onClick={handleDemoToggle}
              disabled={toggleLoading}
              variant={hasProAccess ? 'outline' : 'primary'}
              iconName={hasProAccess ? 'ArrowDown' : 'ArrowUp'}
              iconPosition="left"
              size="sm"
            >
              {toggleLoading 
                ? 'Switching...' 
                : hasProAccess 
                  ? 'Switch to Free Plan' :'Switch to Pro Plan'
              }
            </Button>
            <span className="text-xs text-blue-600">
              Current: <strong>{hasProAccess ? 'Pro' : 'Free'}</strong>
            </span>
          </div>
        </div>
      )}

      {/* Subscription Dashboard */}
      <SubscriptionDashboard />
    </div>
  );
};

export default BillingSection;